import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch'
import { useInView } from 'react-intersection-observer'
import { isEmpty } from 'remeda'
import {
  CardContent,
  CollectionContainer,
  Grid,
} from '@components/ApparelCollection/styles'
import { ApparelProduct } from '@components/ApparelProduct/ApparelProduct'
import { useCategoriesGrid } from '@components/CategoriesGrid/CategoriesGridProvider'
// Note: We're NOT importing useCategoryManager here
import { NoResults } from '../AlgoliaSearch/styles'
import type { FormattedVariantProps } from '../AlgoliaSearch/types'
import { formatAlgoliaHit } from '../AlgoliaSearch/utils'

interface SearchResultsProps {
  showSingleVariant?: boolean
  showProductType?: boolean
  showVariantSelector?: boolean
}

export const SearchResultsList = ({
  showSingleVariant = false,
  showProductType = true,
  showVariantSelector = false,
}: SearchResultsProps) => {
  const { t } = useTranslation('collection-filters')
  const { results, hits, isLastPage, showMore } =
    useInfiniteHits<FormattedVariantProps>()
  const { status } = useInstantSearch()

  const [loadMoreStartRef, inViewNextStart] = useInView({
    rootMargin: `300px 0px 0px 0px`,
  })

  const [loadMoreEndRef, inViewNextEnd] = useInView({
    rootMargin: `0px 0px 300px 0px`,
  })

  const { frontImage: frontImageToShow } = useCategoriesGrid()

  const inViewNext = useMemo(
    () => inViewNextStart || inViewNextEnd,
    [inViewNextStart, inViewNextEnd],
  )

  useEffect(() => {
    if (isLastPage) {
      return
    }

    // Check que status === 'idle' no dé problemas
    if (inViewNext && status === 'idle') {
      showMore()
    }
  }, [inViewNext, showMore, isLastPage, status])

  const hitsPerPage = results?.hitsPerPage ?? 20

  if (status === 'stalled') {
    return (
      <CollectionContainer>
        <Grid>
          {Array.from({ length: 20 }).map((_, index) => (
            <CardContent key={index}>
              <ApparelProduct
                loading
                showSingleVariant={showSingleVariant}
                showProductType={showProductType}
                frontImageToShow={frontImageToShow}
              />
            </CardContent>
          ))}
        </Grid>
      </CollectionContainer>
    )
  }

  return (
    <CollectionContainer>
      <Grid>
        {/* Per evitare di far vedere gli skeleton "sentinella" del load more, quando le hits non sono valorizzate,
        mostro degli skeleton dummy che non fanno triggerare il load more. Altrimenti ogni volta che carico la pagina parte subito un loadMore */}
        {!isEmpty(hits) &&
          hits.map((hit) => {
            return (
              <CardContent
                key={`filter-result-${String(hit.handle)}`}
                className="search-result-item"
              >
                <ApparelProduct
                  showSingleVariant={showSingleVariant}
                  showProductType={showProductType}
                  showVariantSelector={showVariantSelector}
                  frontImageToShow={frontImageToShow}
                  {...formatAlgoliaHit(hit)}
                />
              </CardContent>
            )
          })}
        {/* See here: https://github.com/algolia/instantsearch/discussions/5358 */}
        {status === 'loading' &&
          [...Array(hitsPerPage)].map((_, i) => {
            return (
              <CardContent key={`filter-result-current-${String(i)}`}>
                <ApparelProduct
                  loading
                  showSingleVariant={showSingleVariant}
                  showProductType={showProductType}
                  frontImageToShow={frontImageToShow}
                />
              </CardContent>
            )
          })}
        {/* TODO: stilizzare l'assenza di risultati */}
        {status === 'idle' && isEmpty(hits) && (
          <NoResults>{t('results', { count: 0 })}</NoResults>
        )}
        {/* Metto solo 3 skeleton per evitarmi tutto il problema del restare "incastrati" in mezzo agli skeleton.
        Ci sono dei casi in cui si rimane bloccati in mezzo ai 20 skeleton perché inView resta a true.
        Lo useEffect quindi non viene scatenato. Una possibile soluzione da esplorare è usare lo status idle.
        Se lo status cambia in idle e inView è ancora true, vuol dire che devo caricare altri risultati ancora */}
        {isLastPage
          ? null
          : [...Array(3)].map((_, i, arr) => {
              return (
                <CardContent
                  key={`filter-result-next-${results?.page ?? 1}-${String(i)}`}
                  {...(i === 0 ? { ref: loadMoreStartRef } : {})}
                  {...(arr.length - 1 === i ? { ref: loadMoreEndRef } : {})}
                >
                  <ApparelProduct
                    loading
                    showSingleVariant={showSingleVariant}
                    showProductType={showProductType}
                    frontImageToShow={frontImageToShow}
                  />
                </CardContent>
              )
            })}
      </Grid>
    </CollectionContainer>
  )
}
